import { CSSProperties, useState } from 'react';

import { appColors, BackrsIcon } from '@backrs_ui/common-ui';

import { PromptItem, DOCUMENT_TYPES, SENDER_TYPE } from '@backrs_ui/grow-ui/src/models/models';
import { addOfficeHoursMessage } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getSessionState,
  getActiveLubavSession,
  createSessionAction,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import { checkForUserDocument } from '@backrs_ui/grow-ui/src/utils/checkForUserDocument';

import { Box } from '@mui/material';
import { SxProps, Theme } from '@mui/system';

import { getUploadDocumentMessage } from './FileUpload';
export type RoleForPrompts = 'guidance_counselor' | 'no-default' | 'supercoach' | 'default';

type SuggestedPromptsProps = Readonly<{
  prompts: PromptItem[];
  onSelect: (display: string, input: string) => void;
  asRole?: RoleForPrompts;
  disabled?: boolean;
}>;

const getDefaultPrompts = (role: RoleForPrompts): PromptItem[] => {
  switch (role) {
    case 'guidance_counselor':
      return [
        {
          display: 'Build a college wishlist for my student',
        },
        {
          display: 'Help my student explore career opportunities',
        },
        {
          display: 'Write me a letter of recommendation',
        },
        {
          display: 'Help me recommend classes for my student',
        },
        {
          display: 'Find me scholarships for my students',
        },
        {
          display: "Give feedback on my student's resume",
        },
        {
          display: "Help me improve my student's personal statement",
        },
      ];
    case 'supercoach':
      return [
        {
          display: 'Academic Profile',
          input:
            "Review my student's academic profile including transcript, resumes, and/or grades. Share 3 strengths and 3 areas of opportunity, and comment on any trends. Use bullets where appropriate. Respond conversationally, but include bullet points where appropriate.",
        },
        {
          display: 'Goal Summary',
          input:
            "Tell me about my student's goals, especially recent ones, including if you have any sense of whether any have been achieved. Use bullets where appropriate. Respond conversationally, but include bullet points where appropriate.",
        },
        {
          display: 'Support Ideas',
          input:
            'Share 3 practical ways I might support my student over the next few weeks. Share what I could do, why it might help, and any specific, tangible next steps. Use bullets where appropriate. Respond conversationally, but include bullet points where appropriate.',
        },
        {
          display: 'Strengthen Profile',
          input:
            'Share 5 opportunities my student can strengthen their profile over the next 3-6 months to either gain admission to educational institutions or secure career opportunities. Please share what they can do, why its important, and a few tangible, specific ideas to consider. Use bullets where appropriate. Respond conversationally, but include bullet points where appropriate.\n\nIf they have not shared enough information to formulate a solid response, acknowledge and tell me that they need to add more information on to their Backrs profile including sharing a resume and transcript, completing a Blueprint, adding to their WholeMe profile, and/or sharing more updates.',
        },
        {
          display: 'College Odds',
          input:
            "Help me assess my student's odds of securing admission to a university. First, ask me what university to assess. Then assess their overall odds of admission. Please be candid and share what aspects of their profile may meet that universities qualifications and what elements may not be good enough. If they have not shared enough information to formulate a solid response, acknowledge and tell me that they need to add more information on to their Backrs profile including adding a resume and a recent transcript.",
        },
        {
          display: 'Letter of Rec',
          input:
            "Write a genuine, impactful letter that brings my student's story to life through the specific examples and direct observations I have provided, if I have provided any. Focus on what makes this student truly special, but maintain authenticity - neither overselling nor underselling their achievements. The letter should be no less than 400 words. If certain details are missing, naturally weave in placeholders [in brackets] where needed, but fill in as much as you can with the information I have provided. Your response should feel like it comes from someone who knows the student well, highlighting their character, potential, and genuine experiences. The letter is written by me, so use my name to sign off the letter.",
        },
        {
          display: 'Resume Optimizer',
          input:
            "Review my resume, which you should see in my profile. Share 5-10 specific potential opportunities to improve its efficacy. Focus on ensuring it: \n\n- Uses strong quantifiable achievements\n- Highlights relevant projects, skills and leadership experiences\n- Is clean, concise and well-formatted\n- Is optimized for Applicant Tracking Systems (ATS)\n\nFor each opportunity, use sub-bullet points to describe: \n1. How to improve it\n2. Why it's important\n3. Provide one or two specific examples of changes I can implement, including the exact text to modify and the proposed revisions.\n\nEmphasize the attributes that employers in my field most desire.",
          hidden: true,
          requireDocument: DOCUMENT_TYPES.RESUME,
        },
        {
          display: 'Career Interests',
          input:
            "If you have access to my student's Blueprint, share the minimum required career track along with up to 10 career paths that might be a good fit. Explain in a few sentences why each path may be a good fit using what you know about the student’s interests and preferences from their Blueprint, for example what they do or don't enjoy doing, where they find purpose, and what work settings are more/less appealing. Only use career paths in the Blueprint. Respond conversationally, but include bullet points where appropriate.\n\nIf they have not completed a Blueprint, use the information you have to describe what you know about their career interests. Be transparent about from where you are getting the information in your response. Also, you do not have their Blueprint, end your response by stating “I would be able to share better insight if your student completes a Blueprint.",
          hidden: true,
        },
        {
          display: 'Life Aspirations',
          input:
            "Tell me about my student's life aspirations. Anchor on their Blueprint if you have it. Use bullets where appropriate. Respond conversationally, but include bullet points where appropriate.\n\nIf you do not have enough information to formiulate a solid response, acknowledge and tell me that they need to add more information on to their Backrs profile including completing a Blueprint, WholeMe profile, creating goals, adding a resume, or sharing more updates.",
          hidden: true,
        },
        {
          display: 'Social Capital',
          input:
            "Tell me what you know about my student's social capital including relationships with professional adults in areas they cares about including business, STEM, and patient care. Pay attention to their answers to relationships in their WholeMe profile. If you can tell what type of people they do or don't know, be specific. Use bullets where appropriate. Respond conversationally, but include bullet points where appropriate.\n\nIf you do not have enough information to formulate a solid response, acknowledge that and tell me that they need to add more information to their Backrs profile including completing their WholeMe profile or adding a resume.",
          hidden: true,
        },
        {
          display: 'Jobs Odds',
          input:
            "Help me assess my student's odds of securing an internship or full time job. First, just ask me what job and employer to assess. Then assess their overall odds of landing the opportunity. Please be candid and share what aspects of their profile may meet the likely qualifications and what elements may not be good enough. If they have not shared enough information to please acknowledge and tell me that they need to add more information to their Backrs profile including a resume and transcript.",
          hidden: true,
        },
        {
          display: 'Accountability Email',
          input:
            'Draft an email from me to to my student, focused on your best sense of their current priorities and goals. If they have not shared enough information please do your best to develop some options but acknowledge that you are making an educated guess and need them to build out their profile, goals, and/or complete a Blueprint.',
          hidden: true,
        },
      ];
    case 'no-default':
      return [];
    default:
      return [
        {
          display: 'Recommend colleges based on my academic profile',
        },
        {
          display: 'Help me explore career opportunities',
        },
        {
          display: 'What are my chances of getting into a top university in my field',
        },
        {
          display: 'Find me a scholarship based on my qualifications',
        },
        {
          display: 'Give me tips to study better',
        },
      ];
  }
};

export const SuggestedPrompts = ({
  prompts = [],
  onSelect,
  asRole = 'default',
  disabled = false,
}: SuggestedPromptsProps) => {
  const activeSession = useAppSelector(getActiveLubavSession);

  const { coachingUser } = useAppSelector(
    (state) => getSessionState(state, activeSession).coachLubav,
  );
  const dispatch = useAppDispatch();

  const [showHidden, setShowHidden] = useState(false);

  if (!activeSession || (asRole === 'supercoach' && !coachingUser)) {
    return null;
  }

  const defaultPrompts = getDefaultPrompts(asRole);
  const displayPrompts = prompts.length > 0 && asRole !== 'supercoach' ? prompts : defaultPrompts;

  const visiblePrompts = showHidden
    ? displayPrompts
    : displayPrompts.filter((prompt) => !prompt.hidden);

  const hasHiddenPrompts = displayPrompts.some((prompt) => prompt.hidden);

  return (
    <Box sx={promptsContainerStyle}>
      <Box sx={promptLayoutStyle}>
        <Box sx={promptButtonsGrid}>
          {visiblePrompts.map((prompt, index) => (
            <Box
              key={index}
              sx={promptButtonStyle}
              onClick={async () => {
                if (disabled) return;

                if (prompt.requireDocument) {
                  if (!coachingUser) return;

                  const hasDocument = await checkForUserDocument(
                    parseInt(coachingUser?.id),
                    prompt.requireDocument,
                  );

                  if (!hasDocument) {
                    dispatch(
                      createSessionAction(
                        addOfficeHoursMessage,
                        activeSession,
                      )({
                        text: getUploadDocumentMessage({
                          documentType: prompt.requireDocument,
                          isCoach: true,
                        }),
                        sender: SENDER_TYPE.AI,
                      }),
                    );

                    return;
                  }
                }

                onSelect(prompt.display, prompt.input || '');
                setShowHidden(false);
              }}
            >
              <BackrsIcon
                name="IconPlusBlue"
                alt="Plus"
                height={14}
                width={14}
                color={appColors.color.blue1}
                style={iconStyle}
              />
              <span>{prompt.display}</span>
            </Box>
          ))}
        </Box>
        {hasHiddenPrompts && (
          <Box sx={moreButtonStyle} onClick={() => setShowHidden((prev) => !prev)}>
            {showHidden ? 'Show Less' : 'More'}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const promptsContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: '8px',
  padding: '10px 0px',
  fontSize: '11pt',
  paddingRight: '40px',
};

const promptButtonStyle: SxProps<Theme> = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  width: 'fit-content',
  padding: '8px 10px',
  lineHeight: 1.2,
  fontWeight: '600',
  color: '#000',
  backgroundColor: appColors.color.blue4,
  border: '2px solid transparent',
  borderRadius: '12px',
  cursor: 'pointer',
  '&:hover': {
    border: `2px solid ${appColors.color.blue1}`,
  },
};

const promptButtonsGrid: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: '8px',
};

const moreButtonStyle: SxProps<Theme> = {
  alignSelf: 'flex-start',
  marginTop: '8px',
  padding: '6px 16px',
  fontSize: '11pt',
  fontWeight: '600',
  backgroundColor: 'transparent',
  borderRadius: '12px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: appColors.color.blue4,
  },
};

const iconStyle: CSSProperties = {
  flexShrink: 0,
  marginRight: '12px',
};

const promptLayoutStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
};
